import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { resolveAssetSrc, toAnchor } from './stbMarkdownUtils';

const headingAnchor = (children: React.ReactNode): string | undefined =>
  toAnchor(
    React.Children.toArray(children)
      .map((child) => (typeof child === 'string' ? child : ''))
      .join('')
  );

const StbMarkdown = ({
  children,
  assetBase,
}: {
  children: string;
  /** Prefix for relative image paths, e.g. `/docs/end-user/`. */
  assetBase?: string;
}) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node, children, ...props }) => (
          <h1 className="text-xl font-semibold mt-2 mb-3" data-anchor={headingAnchor(children)} {...props}>
            {children}
          </h1>
        ),
        h2: ({ node, children, ...props }) => (
          <h2 className="text-lg font-semibold mt-3 mb-2" data-anchor={headingAnchor(children)} {...props}>
            {children}
          </h2>
        ),
        h3: ({ node, children, ...props }) => (
          <h3 className="text-base font-semibold mt-3 mb-2" data-anchor={headingAnchor(children)} {...props}>
            {children}
          </h3>
        ),
        img: ({ node, src, alt, ...props }) => (
          <img src={resolveAssetSrc(src, assetBase)} alt={alt || ''} loading="lazy" {...props} />
        ),
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2" {...props} />,
        li: ({ node, ...props }) => <li className="my-1" {...props} />,
        p: ({ node, ...props }) => <p className="my-2" {...props} />,
        a: ({ node, ...props }) => (
          <a className="text-link underline" target="_blank" rel="noreferrer" {...props} />
        ),
        code: ({ node, ...props }) => <code className="bg-gray-100 px-1 rounded" {...props} />,
        pre: ({ node, ...props }) => (
          <pre className="bg-gray-100 p-2 rounded overflow-auto my-2" {...props} />
        ),
        table: ({ node, ...props }) => (
          <div className="overflow-x-auto my-3">
            <table className="min-w-full border border-gray-300 text-sm" {...props} />
          </div>
        ),
        thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
        tbody: ({ node, ...props }) => <tbody {...props} />,
        tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
        th: ({ node, ...props }) => (
          <th className="border border-gray-300 px-3 py-2 text-left font-semibold" {...props} />
        ),
        td: ({ node, ...props }) => (
          <td className="border border-gray-300 px-3 py-2 align-top" {...props} />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
};

export default StbMarkdown;